import React from 'react'
import {ClientMetricCard} from './ClientMetricCard'
import {StatusBarChart} from './StatusBarChart'
import type {OPIMetrics, StatusBucket} from './_requests'

interface Props {
  metrics: OPIMetrics
  statusBuckets: StatusBucket[]
  periodLabel: string
}

const PhoneIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
  </svg>
)

const ClockIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <polyline points='12 6 12 12 16 14' />
  </svg>
)

const TrendIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='23 6 13.5 15.5 8.5 10.5 1 18' />
    <polyline points='17 6 23 6 23 12' />
  </svg>
)

export const OPIMetricsSection: React.FC<Props> = ({metrics, statusBuckets, periodLabel}) => {
  const formatConnectionTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return s > 0 ? `${m}m ${s}s` : `${m}m`
  }

  return (
    <section className='cp-section'>
      <div className='cp-section__header'>
        <div className='cp-section__icon'>
          <PhoneIcon />
        </div>
        <div>
          <h2 className='cp-section__title'>Over the Phone Interpretation (OPI) - {periodLabel}</h2>
          <p className='cp-section__subtitle'>Over the Phone Interpretation performance metrics.</p>
        </div>
      </div>

      <div className='cp-metrics-row'>
        <ClientMetricCard
          title='Total Calls'
          value={metrics.totalCalls.toLocaleString()}
          subtext={`${metrics.totalMinutes.toLocaleString()} minutes total`}
          icon={<PhoneIcon />}
          tooltip={`Total number of OPI calls placed in the selected period (${periodLabel}), with cumulative connected minutes shown underneath.`}
        />
        <ClientMetricCard
          title='Fulfillment'
          value={`${metrics.fulfillmentRate}%`}
          icon={<ClockIcon />}
          highlight
          tooltip='Calls successfully connected to an interpreter divided by total calls requested, multiplied by 100.'
        />
        <ClientMetricCard
          title='Connection Time'
          value={formatConnectionTime(metrics.avgConnectionTimeSeconds)}
          subtext='Average'
          icon={<TrendIcon />}
          tooltip='Average time from when a caller requests an interpreter to when the interpreter is connected.'
        />
        <ClientMetricCard
          title='AVG Duration'
          value={metrics.avgDuration || '0m'}
          icon={<ClockIcon />}
          tooltip={`Average length of an OPI call across the selected period (${periodLabel}).`}
        />
      </div>

      <StatusBarChart
        title='Over The Phone Calls By Status'
        data={statusBuckets}
        completedLabel='Connected'
        cancelledLabel='Abandoned'
      />
    </section>
  )
}
